import { Link } from "react-router-dom";
import { ScrollReveal } from "@/components/base/ScrollAnimations";

const navPills = [
  { label: "Home", href: "#" },
  { label: "PIIC", href: "#programmes" },
  { label: "Ecosystem", href: "#partners" },
  { label: "Expo", href: "#expo" },
];

export default function HeroSection() {
  const handleScroll = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (href.startsWith("#") && href !== "#") {
      e.preventDefault();
      const target = document.querySelector(href);
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#F8F5F0]">
      {/* Textile fabric background image - warm cream tones, no blue or green */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Elegant%20minimal%20textile%20fabric%20texture%20background%2C%20soft%20woven%20linen%20threads%20in%20warm%20cream%20beige%20and%20ivory%20tones%2C%20subtle%20fiber%20detail%2C%20luxurious%20natural%20fabric%20surface%2C%20macro%20photography%20style%2C%20warm%20ambient%20lighting%2C%20soft%20shadows%2C%20no%20blue%20no%20green%20colors%2C%20high-end%20textile%20industry%20aesthetic%2C%20clean%20and%20sophisticated%2C%20horizontal%20composition&width=1920&height=1080&seq=1&orientation=landscape"
          alt=""
          className="w-full h-full object-cover opacity-40"
        />
        {/* Soft warm overlay to unify tone */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#F8F5F0]/60 via-transparent to-[#F8F5F0]/80" />
      </div>


      {/* Content */}
      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 pt-20 pb-16">
        <div className="max-w-5xl mx-auto text-center">
          <ScrollReveal>
            <div className="flex flex-col items-center">
              <img
                src="https://storage.readdy-site.link/project_files/3ad77496-7e33-4bee-aed3-853667869f76/3a6b7789-d222-4345-bb6d-4232140708c8_TIE1.png?v=90453bddcbe5657cab713355118e545f"
                alt="Textile Innovation Exchange"
                className="h-40 md:h-48 lg:h-52 w-auto object-contain"
              />
            </div>
          </ScrollReveal>

          <ScrollReveal>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-tie-teal/10 border border-tie-teal/20 mb-6 md:mb-8">
              <span className="w-2 h-2 rounded-full bg-tie-teal animate-pulse" />
              <span className="text-xs md:text-sm text-tie-teal font-medium tracking-wide uppercase">
                Bangladesh-rooted Global Platform
              </span>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={150}>
            <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-tie-text-primary leading-tight" spellCheck="false">
              Turning Innovativeness into the{" "}
              <span className="text-tie-teal">Core Competence</span>
            </h1>
          </ScrollReveal>

          <ScrollReveal delay={300}>
            <p className="mt-6 md:mt-8 text-base md:text-lg lg:text-xl text-tie-text-secondary max-w-3xl mx-auto leading-relaxed">
              TIE connects factories, experts, academia, brands, solution
              providers, and institutions to implement, measure, scale, and
              recognize innovation across Bangladesh's textile ecosystem.
            </p>
          </ScrollReveal>

          <ScrollReveal delay={450}>
            <div className="mt-8 md:mt-10 flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/programs"
                className="inline-flex items-center justify-center px-8 py-4 rounded-full bg-tie-teal text-white font-medium text-sm md:text-base hover:bg-tie-teal-dark transition-all duration-300 whitespace-nowrap"
              >
                Explore TIE Ecosystem
                <i className="ri-arrow-right-line ml-2" />
              </Link>
              <Link
                to="/collaborate"
                className="inline-flex items-center justify-center px-8 py-4 rounded-full bg-white text-tie-text-primary border border-tie-grey-dark font-medium text-sm md:text-base hover:bg-tie-grey-light transition-all duration-300 whitespace-nowrap"
              >
                Collaborate with TIE
                <i className="ri-arrow-right-line ml-2" />
              </Link>
            </div>

            {/* Navigation pill buttons */}
            <div className="mt-6 md:mt-8 inline-flex flex-wrap items-center justify-center gap-2 md:gap-3">
              {navPills.map((pill) => (
                <a
                  key={pill.label}
                  href={pill.href}
                  onClick={(e) => handleScroll(e, pill.href)}
                  className="inline-flex items-center justify-center px-5 py-2 rounded-full bg-tie-navy/10 text-tie-navy font-medium text-xs md:text-sm hover:bg-tie-navy hover:text-white transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  {pill.label}
                </a>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </div>

      {/* Bottom Tagline */}
      <div className="absolute bottom-0 left-0 right-0 z-10">
        <div className="w-full px-4 md:px-8 lg:px-12 py-6 md:py-8">
          <ScrollReveal>
            <p className="text-xs md:text-sm text-tie-text-muted font-medium tracking-wider uppercase text-center">
              Systematic &middot; Measurable &middot; Evidence-Based Innovation
            </p>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}